import React, { useState, useRef } from 'react';
import { Settings, RefreshCw, Plus, X, Trash2, Power, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { CorrectionRule } from '@/services/kbService';
import { useEntityTypeOptions } from '@/hooks/useEntityTypeOptions';
import { QAErrorAlert } from './QAErrorAlert';

export interface QARulesTabProps {
  rules: CorrectionRule[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onAddRule?: (rule: {
    name: string;
    from_type: string;
    to_type: string;
    pattern?: string;
    confidence?: number;
    action?: string;
    priority?: number;
  }) => void;
  onDeleteRule?: (ruleId: string) => void;
  onToggleRule?: (ruleId: string) => void;
  onEditRule?: (ruleId: string, rule: {
    name: string;
    from_type: string;
    to_type: string;
    pattern?: string;
    confidence?: number;
    action?: string;
    priority?: number;
  }) => void;
}

export const QARulesTab: React.FC<QARulesTabProps> = ({
  rules,
  loading,
  error,
  onRefresh,
  onAddRule,
  onDeleteRule,
  onToggleRule,
  onEditRule,
}) => {
  const { entityTypeOptions } = useEntityTypeOptions();
  const formRef = useRef<HTMLDivElement>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [ruleName, setRuleName] = useState('');
  const [fromType, setFromType] = useState('');
  const [toType, setToType] = useState('');
  const [pattern, setPattern] = useState('');
  const [confidence, setConfidence] = useState(85);
  const [action, setAction] = useState('review_queue');
  const [priority, setPriority] = useState<string>('0');
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setEditingRuleId(null);
    setRuleName('');
    setFromType('');
    setToType('');
    setPattern('');
    setConfidence(85);
    setAction('review_queue');
    setPriority('0');
  };

  const handleEditRule = (rule: CorrectionRule) => {
    setEditingRuleId(rule.id);
    setRuleName(rule.name || '');
    setFromType(rule.from_type || rule.entity_type || '');
    setToType(rule.to_type || rule.correction || '');
    setPattern(rule.pattern || '');
    setConfidence(Math.round((rule.confidence ?? 0.85) * 100));
    setAction(rule.action || 'review_queue');
    setPriority(String(rule.priority ?? 0));
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
  };

  const handleSubmit = async () => {
    if (!ruleName || !fromType || !toType) return;
    setSubmitting(true);
    try {
      const ruleData = {
        name: ruleName,
        from_type: fromType,
        to_type: toType,
        pattern: pattern || undefined,
        confidence: confidence / 100,
        action,
        priority: parseInt(priority) || 0,
      };
      if (editingRuleId && onEditRule) {
        await onEditRule(editingRuleId, ruleData);
      } else if (onAddRule) {
        await onAddRule(ruleData);
      }
      resetForm();
      setShowForm(false);
    } finally {
      setSubmitting(false);
    }
  };

  const renderRuleForm = () => (
    <Card ref={formRef} className="mb-4 border-primary/30 bg-primary/10">
      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Rule Name <span className="text-red-500">*</span></Label>
            <Input
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              placeholder="e.g., Fix plant misclassification"
            />
          </div>
          <div className="space-y-2">
            <Label>Pattern</Label>
            <Input
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="Optional regex pattern"
            />
          </div>
          <div className="space-y-2">
            <Label>From Type <span className="text-red-500">*</span></Label>
            <Select value={fromType} onValueChange={setFromType}>
              <SelectTrigger>
                <SelectValue placeholder="Source entity type" />
              </SelectTrigger>
              <SelectContent className="dark">
                {entityTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>To Type <span className="text-red-500">*</span></Label>
            <Select value={toType} onValueChange={setToType}>
              <SelectTrigger>
                <SelectValue placeholder="Target entity type" />
              </SelectTrigger>
              <SelectContent className="dark">
                {entityTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <Input
              type="number"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              placeholder="0"
              min={0}
            />
          </div>
          <div className="space-y-2">
            <Label>Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark">
                <SelectItem value="review_queue">Review Queue</SelectItem>
                <SelectItem value="auto_correct">Auto Correct</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Confidence Threshold</Label>
            <div className="flex items-center gap-3">
              <Slider
                className="flex-1"
                value={[confidence]}
                onValueChange={([v]) => setConfidence(v)}
                min={0}
                max={100}
                step={5}
              />
              <span className="text-sm font-medium w-12 text-right shrink-0">{confidence}%</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => { resetForm(); setShowForm(false); }}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={submitting || !ruleName || !fromType || !toType}
          >
            {submitting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
            {editingRuleId ? 'Update Rule' : 'Create Rule'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
      {error && (
        <QAErrorAlert
          message="Failed to load correction rules"
          error={error}
          onRetry={onRefresh}
        />
      )}

      <div className="flex items-center gap-2 mb-4">
        {onAddRule && (
          <Button
            variant={showForm && !editingRuleId ? 'secondary' : 'default'}
            size="sm"
            className="text-xs sm:text-sm"
            onClick={() => {
              if (showForm && !editingRuleId) {
                resetForm();
                setShowForm(false);
              } else {
                resetForm();
                setShowForm(true);
              }
            }}
          >
            {showForm && !editingRuleId ? <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" /> : <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
            {showForm && !editingRuleId ? 'Cancel' : 'Add Rule'}
          </Button>
        )}

        {!(showForm && !editingRuleId) && (
          <Button
            variant="outline"
            size="sm"
            className="text-xs sm:text-sm"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-spin" /> : <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
            Refresh
          </Button>
        )}
      </div>

      {/* Add Rule Form (top - only for new rules, not editing) */}
      {showForm && !editingRuleId && renderRuleForm()}

      {loading ? (
        <LoadingSpinner message="Loading correction rules..." fullScreen={false} />
      ) : rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 sm:py-12">
          <Settings className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
          <p className="text-sm sm:text-lg font-medium">No correction rules configured</p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {rules.map((rule) => (
            <React.Fragment key={rule.id}>
            <Card className={`hover:bg-muted/20 transition-colors ${editingRuleId === rule.id ? 'border-primary/30 bg-primary/10' : ''}`}>
              <CardContent className="p-3 sm:py-4 sm:px-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${rule.is_active ? 'bg-primary/15' : 'bg-secondary'}`}>
                    <Settings className={`h-4 w-4 sm:h-5 sm:w-5 ${rule.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                      <span className="font-medium text-sm sm:text-base truncate">{rule.name || 'Unnamed Rule'}</span>
                      {rule.entity_type && (
                        <Badge variant="outline" className="text-[10px] sm:text-xs">{rule.entity_type}</Badge>
                      )}
                      <span className="text-xs sm:text-sm text-muted-foreground">Priority: {rule.priority ?? 0}</span>
                    </div>
                    <div className="space-y-1 text-xs sm:text-sm">
                      <p className="break-all"><code className="px-1 py-0.5 bg-muted rounded text-[10px] sm:text-xs">Pattern: {rule.pattern || 'N/A'}</code></p>
                      <p className="break-all"><code className="px-1 py-0.5 bg-muted rounded text-[10px] sm:text-xs">Correction: {rule.correction || 'N/A'}</code></p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4 flex-shrink-0">
                    <Badge variant={rule.is_active ? 'default' : 'secondary'} className="text-[10px] sm:text-xs">
                      {rule.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </Badge>
                    <span className="text-[10px] sm:text-sm text-muted-foreground whitespace-nowrap">Applied {rule.times_applied ?? 0} times</span>
                    <div className="flex items-center gap-1">
                      {editingRuleId === rule.id ? (
                        <Badge variant="outline" className="text-[10px] sm:text-xs text-primary border-primary/30">
                          Editing...
                        </Badge>
                      ) : (
                        <>
                          {onEditRule && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10"
                                    onClick={() => handleEditRule(rule)}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="dark">Edit rule</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {onToggleRule && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className={`h-8 w-8 ${rule.is_active ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10' : 'text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10'}`}
                                    onClick={() => onToggleRule(rule.id)}
                                  >
                                    <Power className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="dark">{rule.is_active ? 'Deactivate rule' : 'Activate rule'}</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {onDeleteRule && (
                            <AlertDialog>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                  </TooltipTrigger>
                                  <TooltipContent className="dark">Delete rule</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <AlertDialogContent className="dark bg-background text-foreground">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Correction Rule</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete the rule <strong>{rule.name || 'Unnamed Rule'}</strong>? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => onDeleteRule(rule.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Inline edit form below this rule */}
            {showForm && editingRuleId === rule.id && renderRuleForm()}
            </React.Fragment>
          ))}
        </div>
      )}
    </>
  );
};
